import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function AutostyleNearMePage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '', // No text filter
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : 'Near Me';

    const pageTitle = hasLocationFilter
        ? `AutoStyle ${locationLabel} | Car Accessories, Styling & Custom Parts`
        : 'AutoStyle Near Me | Car Accessories, Styling & Custom Parts';

    const pageDescription = hasLocationFilter
        ? `Find your nearest AutoStyle store in ${locationLabel}. Car parts, car spares, Car accessories, styling parts, custom modifications, audio systems, and more. Quality products and professional installation.`
        : 'Find your nearest AutoStyle store in South Africa. Car parts, car spares, Car accessories, styling parts, custom modifications, audio systems, and more. Quality products and professional installation.';

    const baseUrl = `${ENV.DOMAIN_URL}/autostyle-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Midrand', 'Soweto'],
        Pretoria: ['Centurion', 'Akasia', 'Soshanguve', 'Mamelodi'],
        'Cape Town': ['Bellville', 'Somerset West', 'Paarl', 'Stellenbosch', 'Brackenfell'],
        Durban: ['Pinetown', 'Phoenix', 'Chatsworth', 'Umhlanga'],
    };

    const faqItems = [
        {
            question: 'Where can I find AutoStyle stores near me in South Africa?',
            answer:
                'You can find AutoStyle stores across South Africa using our store locator. Filter by province, city, or suburb to find your nearest AutoStyle location. Major cities include Johannesburg, Cape Town, Durban, Pretoria, and Port Elizabeth.',
        },
        {
            question: 'What products and services does AutoStyle offer?',
            answer:
                'AutoStyle offers a wide range of car accessories and styling products including body kits, spoilers, side skirts, alloy wheels, sound systems, security systems, interior upgrades, LED lighting, performance parts, and custom modifications. Many stores also provide professional fitment and installation services.',
        },
        {
            question: 'Does AutoStyle install the products they sell?',
            answer:
                'Yes, most AutoStyle stores offer professional installation services for the products they sell. This includes fitment of body kits, sound systems, security systems, wheels, and various accessories. Contact your local AutoStyle store for specific installation services and pricing.',
        },
        {
            question: 'Can AutoStyle help with custom car modifications?',
            answer:
                'Yes, AutoStyle specializes in custom car modifications and styling. Their experienced staff can help you design and implement custom projects including body modifications, performance upgrades, interior customization, audio and security installations, and more. Visit your nearest store to discuss your customization needs.',
        },
        {
            question: 'What brands does AutoStyle carry?',
            answer:
                'AutoStyle carries a wide selection of quality brands for car accessories and styling products. This includes both international and local brands for wheels, audio equipment, body kits, lighting, security systems, and performance parts. Check with your local AutoStyle store for specific brand availability.',
        },
    ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `AutoStyle ${titleLocationLabel}`,
    description: pageDescription,
    url: canonicalUrl,
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="AutoStyle near me, AutoStyle South Africa, car accessories near me, car styling, custom modifications, alloy wheels, sound systems, body kits, car upgrades, AutoStyle stores" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Structured Data — merged into single @graph to avoid duplicate-field error */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              { ...structuredData, '@context': undefined },
              { ...faqStructuredData, '@context': undefined },
            ],
          }) }}
        />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 py-16 text-white md:py-24">
            <div className="container relative z-10">
                <div className="mx-auto max-w-4xl text-center">
                    <h1 className="mb-4 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                        AutoStyle {titleLocationLabel}
                    </h1>
                    <p className="mb-8 text-lg leading-relaxed text-blue-50 md:text-xl">
                        Car Accessories, Styling & Custom Modifications
                    </p>
                    <div className="mb-6 flex flex-wrap justify-center gap-3 text-sm md:text-base">
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Body Kits & Styling
                        </div>
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Sound Systems
                        </div>
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Professional Fitment
                        </div>
                    </div>
                    <div className="mb-6 grid grid-cols-2 gap-3 text-center text-sm text-blue-100 sm:grid-cols-4 md:grid-cols-6">
                        {quickCities.map((city) => (
                            <Link
                                key={city.name}
                                href={`/autostyle-near-me/autostyle/${city.province}/${slugify(city.name)}`}
                                className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-blue-300"
                            >
                                {`AutoStyle ${city.name}`}
                            </Link>
                        ))}
                    </div>
                    <div className="mt-5 grid gap-3 text-xs text-blue-100 sm:grid-cols-3">
                        <div className="rounded-lg border border-white/10 px-3 py-2">Wheels & styling</div>
                        <div className="rounded-lg border border-white/10 px-3 py-2">Audio & security</div>
                        <div className="rounded-lg border border-white/10 px-3 py-2">Custom modifications</div>
                    </div>
                </div>
            </div>
        </section>

        <div className="my-10">
            <div className="container" id="business-filters">
                <BusinessList
                    searchParams={searchParams}
                    defaultCategoryIds="5018"
                    initialBusinesses={businesses}
                    initialPagination={pagination}
                />
            </div>
        </div>

        <section className="container my-12">
            <div className="grid gap-8 lg:grid-cols-12">
                <div className="lg:col-span-8">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-2xl font-semibold text-slate-900">Your Trusted AutoStyle Store</h2>
                        <p className="mt-3 text-sm text-slate-600">
                            Looking for AutoStyle {locationSuffix}? CarSparesEzyFind helps you locate
                            AutoStyle stores and car accessory specialists across South Africa. AutoStyle offers a comprehensive 
                            range of car accessories, styling parts, custom modifications, audio systems, security systems, 
                            wheels, body kits, and more at competitive prices. Use the filters to find your nearest AutoStyle 
                            store by province, city, or suburb.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            Whether you need an AutoStyle near me in Sandton, an AutoStyle store in
                            Roodepoort, or AutoStyle car accessories in Randburg, this page helps you find the most
                            convenient location. AutoStyle stores stock quality branded products including alloy wheels, 
                            mag wheels, body kits, spoilers, side skirts, LED lighting, xenon lights, sound systems, 
                            amplifiers, subwoofers, car alarms, tracking systems, and interior accessories. Many locations 
                            also offer professional fitment and installation services by experienced technicians.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            Popular searches include AutoStyle Johannesburg Gauteng, AutoStyle car accessories Johannesburg,
                            AutoStyle wheels Cape Town, and AutoStyle sound systems Durban.
                            Products include alloy wheels and tyres, body kits and styling parts, spoilers and side skirts, 
                            sound systems and amplifiers, car security and tracking, LED and xenon lighting, performance 
                            parts and upgrades, interior accessories and trim, custom upholstery and panels. Visit your 
                            nearest AutoStyle for expert advice, quality products, and professional installation.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            AutoStyle stores are known for their wide selection and knowledgeable staff who can help you 
                            customize your vehicle to your exact specifications. Most locations offer professional installation 
                            for all products sold, ensuring proper fitment and functionality. Whether you're looking for subtle 
                            enhancements or a complete custom build, AutoStyle has the products and expertise to bring your 
                            vision to life. Check with your local AutoStyle store for specific product availability, services, 
                            operating hours, and current promotions.
                        </p>
                    </div>
                </div>
                <div className="lg:col-span-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-slate-900">AutoStyle Products & Services</h3>
                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                            <li>Alloy wheels & tyres</li>
                            <li>Body kits & styling</li>
                            <li>Spoilers & side skirts</li>
                            <li>Sound systems & audio</li>
                            <li>Car security & tracking</li>
                            <li>LED & xenon lighting</li>
                            <li>Performance upgrades</li>
                            <li>Interior accessories</li>
                            <li>Custom upholstery</li>
                            <li>Window tinting</li>
                            <li>Car care products</li>
                            <li>Professional fitment</li>
                            <li>Installation services</li>
                            <li>Custom modifications</li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <section className="container my-12">
            <div className="grid gap-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">AutoStyle by Province</h2>
                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            {quickProvinces.map((province) => (
                                <Link
                                    key={province}
                                    href={`/autostyle-near-me/autostyle/${slugify(province)}`}
                                    className="rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700 hover:border-blue-600 hover:text-blue-600"
                                >
                                    {`AutoStyle ${province}`}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-7">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">
                            Frequently Asked Questions (AutoStyle SEO)
                        </h2>
                        <div className="mt-4 space-y-4">
                            {faqItems.map((item) => (
                                <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                    <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <DownloadApp />
      </div>
    </>
  );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '5018',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
